// Back steps make the phone's back gesture (and a browser's Back) work
// inside the app, where every screen lives on one address (UX audit
// 2026-10-19). Each thing a back should undo — a sheet, the menu, a client's
// profile, a tab switch — pushes one history entry while it is up. A back
// gesture pops that entry, and the step on top is asked to undo itself. With
// no step left, back leaves the app, as it does from any app's home screen.
//
// A step closed by its own button (or backdrop) is released: its entry stays
// in the browser's history, but it is dead. The next step pushed reuses the
// dead entry, and a back gesture that lands on one skips past it, so a tap
// and a gesture never go out of step with each other.

import React from 'react'

type Step = {
  id: number
  // onBack undoes the step; false refuses (mid-save), and the step stays.
  onBack: () => boolean
  dead: boolean
}

const steps: Step[] = []
let nextId = 1
let listening = false

function stateOf(id: number) {
  return { harmoviStep: id }
}

function landedOn(state: unknown): number {
  return state && typeof state === 'object' && typeof (state as { harmoviStep?: unknown }).harmoviStep === 'number'
    ? (state as { harmoviStep: number }).harmoviStep
    : 0
}

// onPop runs when the browser has moved to an earlier entry: every step above
// the entry landed on is undone, top first. A refusal puts that step's entry
// back and stops; a back that only skipped dead steps goes one further.
function onPop(event: PopStateEvent) {
  const landed = landedOn(event.state)
  let undone = false
  let skipped = false
  while (steps.length > 0 && steps[steps.length - 1].id > landed) {
    const top = steps[steps.length - 1]
    if (top.dead) {
      steps.pop()
      skipped = true
      continue
    }
    if (top.onBack()) {
      steps.pop()
      undone = true
    } else {
      window.history.pushState(stateOf(top.id), '')
      return
    }
  }
  if (skipped && !undone) window.history.back()
}

export function pushBackStep(onBack: () => boolean): () => void {
  if (!listening) {
    window.addEventListener('popstate', onPop)
    listening = true
  }
  const id = nextId++
  const top = steps[steps.length - 1]
  if (top?.dead) {
    steps.pop()
    window.history.replaceState(stateOf(id), '')
  } else {
    window.history.pushState(stateOf(id), '')
  }
  const step: Step = { id, onBack, dead: false }
  steps.push(step)
  return () => {
    if (steps.includes(step)) step.dead = true
  }
}

// useBackStep is one back step for as long as the component is up: a screen
// with a way back that is not a dialog (a client's profile). onBack undoes
// it; returning false refuses.
export function useBackStep(onBack: () => boolean | void, active = true): void {
  const onBackRef = React.useRef(onBack)
  onBackRef.current = onBack
  React.useEffect(() => {
    if (!active) return
    return pushBackStep(() => onBackRef.current() !== false)
  }, [active])
}
